import { useEffect, useRef, useState } from 'react';

const testimonials = [
  {
    stars: 5,
    quote: "We'd been through two management companies that charged base fees and delivered flat results. 360 came in, restructured our contract, and hit our RevPAR target within the first quarter thanks to their Hotel Revenue Management Service. For the first time, my operator actually felt like a partner.",
    name: 'Robert K.',
    title: 'Hotel Owner',
    location: 'Fort Myers, FL',
    focus: 'RevPAR & Operations',
    icon: 'ri-line-chart-line',
  },
  {
    stars: 5,
    quote: "Our NOI was underperforming our underwriting by 18%. The 360 team identified the operational gaps, and we saw measurable improvement within 60 days. The asset management overlay was something we didn't know we needed — until we had it.",
    name: 'Maria L.',
    title: 'Private Equity',
    location: 'Sarasota Hospitality Portfolio',
    focus: 'NOI & Asset Value',
    icon: 'ri-building-line',
  },
  {
    stars: 5,
    quote: "I was about to sign a hotel management agreement that would have locked me in for 10 years with no performance triggers. 360 reviewed it in 48 hours, flagged five critical terms, and renegotiated as part of their Hotel Management Service. That review alone was worth more than I can quantify.",
    name: 'James T.',
    title: 'Independent Hotel Developer',
    location: 'Daytona Beach, FL',
    focus: 'Contract Advisory',
    icon: 'ri-file-text-line',
  },
];

export default function Testimonials() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-xl mx-auto mb-16">
          <p className="text-xs font-light text-amber-600 uppercase tracking-widest mb-4" style={{ letterSpacing: '0.25em' }}>
            Partner Quotes
          </p>
          <h2
            className="text-4xl lg:text-5xl font-extralight text-gray-900 leading-tight"
          >
            What Hotel Owners Are Saying About Our Hotel Management Service
          </h2>
        </div>

        {/* Cards */}
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className={`relative flex flex-col p-8 rounded-2xl border border-gray-100 bg-white hover:border-amber-200 transition-all duration-700 group ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Focus tag */}
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-50">
                  <i className={`${t.icon} text-sm text-amber-600`}></i>
                </div>
                <span className="text-xs font-light text-amber-600 uppercase tracking-widest" style={{ letterSpacing: '0.15em' }}>
                  {t.focus}
                </span>
              </div>

              {/* Stars */}
              <div className="flex items-center space-x-0.5 mb-5">
                {Array.from({ length: t.stars }).map((_, si) => (
                  <i key={si} className="ri-star-fill text-amber-500 text-sm"></i>
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-sm font-light text-gray-600 leading-relaxed flex-1 mb-8 italic">
                "{t.quote}"
              </blockquote>

              {/* Author */}
              <div className="pt-6 border-t border-gray-100">
                <p className="text-sm font-normal text-gray-900">{t.name}</p>
                <p className="text-xs font-light text-gray-400 mt-0.5">{t.title} — {t.location}</p>
              </div>

              {/* Hover accent */}
              <div className="absolute bottom-0 left-8 right-8 h-0.5 bg-amber-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full"></div>
            </div>
          ))}
        </div>

        {/* Trust strip */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: 'ri-shield-check-line', text: 'Performance-Guaranteed Contracts' },
            { icon: 'ri-time-line', text: 'Response Within 24 Business Hours' },
            { icon: 'ri-user-star-line', text: 'Senior Partner — Not a Junior Rep' },
            { icon: 'ri-lock-line', text: 'No Commitment Required' },
          ].map((item, i) => (
            <div key={i} className="flex items-center space-x-3 p-4 rounded-xl bg-gray-50">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-lg text-amber-600`}></i>
              </div>
              <span className="text-xs font-light text-gray-600 leading-snug">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
